"use client";

import { useState } from "react";
import { Copy, Check, ChevronDown, ChevronUp } from "lucide-react";
import type { VirtualAccount } from "@/lib/conduit";

type Instruction = NonNullable<VirtualAccount["depositInstructions"]>[number];

function CopyValue({ value, mono = true, wrap = false }: { value: string; mono?: boolean; wrap?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={`group flex min-w-0 items-start gap-2 text-left ${wrap ? "" : "items-center"}`}
      title="Copy"
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      <span
        className={`${mono ? "mono " : ""}min-w-0 text-[13px] ${wrap ? "whitespace-pre-line break-words" : "truncate"}`}
      >
        {value}
      </span>
      {copied ? (
        <Check size={12} className="mt-[3px] shrink-0" style={{ color: "var(--c-green)" }} />
      ) : (
        <Copy size={12} className="mt-[3px] shrink-0 opacity-0 transition-opacity duration-150 group-hover:opacity-60" />
      )}
    </button>
  );
}

function Row({ label, value, mono = true }: { label: string; value?: string | null; mono?: boolean }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-6 py-[7px]">
      <span className="shrink-0 text-[12px] font-light" style={{ color: "var(--c-on-dark-soft)" }}>
        {label}
      </span>
      <CopyValue value={value} mono={mono} />
    </div>
  );
}

/** Full-width row for long values (addresses) — label above, value wraps, never truncates. */
function BlockRow({ label, value, mono = false }: { label: string; value?: string | null; mono?: boolean }) {
  if (!value) return null;
  return (
    <div className="py-[7px]">
      <div className="mb-1 text-[12px] font-light" style={{ color: "var(--c-on-dark-soft)" }}>
        {label}
      </div>
      <CopyValue value={value} mono={mono} wrap />
    </div>
  );
}

const TYPE_LABELS: Record<string, string> = {
  domestic_usd: "domestic transfers",
  swift_usd: "international · swift",
};

/**
 * Deposit instructions on the dark account card. Compact by default (the
 * numbers you wire with); "full instructions" expands beneficiary and bank
 * addresses, fully wrapped — never truncated.
 */
export default function DepositInstructions({
  instructions,
  defaultExpanded = false,
}: {
  instructions: Instruction[];
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  if (!instructions || instructions.length === 0) return null;

  return (
    <div className="mt-2">
      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
        {instructions.map((di, i) => {
          const rails = di.rails ?? [];
          const railNames = rails.map((r) => r.rail).filter(Boolean).join(" · ");
          return (
            <div key={i} className="rounded-[16px] p-4" style={{ background: "rgba(255,255,255,0.06)" }}>
              <div className="mb-1 flex items-center justify-between">
                <span className="eyebrow" style={{ color: "var(--c-green)" }}>
                  {TYPE_LABELS[di.type ?? ""] ?? di.type?.replaceAll("_", " ")}
                </span>
                {railNames && <span className="pill pill-outline !text-white/60">{railNames}</span>}
              </div>
              <div className="divide-y" style={{ borderColor: "var(--c-on-dark-hairline)" }}>
                <Row label="Account number" value={di.accountNumber} />
                {rails.map((r, j) =>
                  r.routingNumber ? (
                    <Row key={j} label={`Routing number${r.rail ? ` (${r.rail})` : ""}`} value={r.routingNumber} />
                  ) : null,
                )}
                <Row label="SWIFT / BIC" value={di.bank?.bic} />
                <Row label="Bank" value={di.bank?.legalName} mono={false} />
                {expanded && (
                  <>
                    <BlockRow label="Beneficiary" value={di.beneficiaryName} />
                    <BlockRow label="Beneficiary address" value={di.beneficiaryAddress} />
                    <BlockRow label="Bank address" value={di.bank?.address} />
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-1 text-[12px] font-medium underline-offset-2 hover:underline"
          style={{ color: "var(--c-green)" }}
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          {expanded ? "hide full instructions" : "full instructions"}
        </button>
        <span className="text-[11px] font-light" style={{ color: "var(--c-on-dark-soft)" }}>
          click any value to copy
        </span>
      </div>
    </div>
  );
}
